import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2, ArrowRight } from 'lucide-react';
import SectionTitle from './SectionTitle';
import Button from './Button';
import config from '../utils/config';


const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentCategory, setCurrentCategory] = useState('all');
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    { id: 'all', name: 'सभी फोटो' },
    { id: 'events', name: 'कार्यक्रम' },
    { id: 'service', name: 'सेवा कार्य' },
    { id: 'community', name: 'सामुदायिक' },
    { id: 'awards', name: 'सम्मान एवं पुरस्कार' }
  ];

  // Fetch gallery data from backend
  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${config.url}/gallery`);
        if (!response.ok) {
          throw new Error('Failed to fetch gallery data');
        }
        const data = await response.json();
        setGalleryData(data);
      } catch (err) {
        console.error('Error fetching gallery data:', err);
        setError('गैलरी डेटा लोड करने में समस्या आई है।');
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  const filteredImages = currentCategory === 'all' 
    ? galleryData.slice(0, 6) // Show only first 6 images on homepage
    : galleryData.filter(img => img.category === currentCategory).slice(0, 6);

  const openLightbox = (index) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % filteredImages.length);
    }
  };

  const prevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? filteredImages.length - 1 : selectedImage - 1);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedImage !== null) {
        if (e.key === 'Escape') {
          closeLightbox();
        } else if (e.key === 'ArrowRight') {
          nextImage();
        } else if (e.key === 'ArrowLeft') {
          prevImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, filteredImages.length]);

  if (loading) {
    return (
      <section id="gallery" className="py-8 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-2 leading-tight">
              <span className="text-orange-600">फोटो </span>
              <span className="text-orange-400">गैलरी</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              विभिन्न कार्यक्रमों और उत्सवों की यादें - हर तस्वीर एक कहानी कहती है।
            </p>
          </div>
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-lg text-gray-600">गैलरी लोड हो रही है...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="gallery" className="py-8 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-2 leading-tight">
              <span className="text-orange-600">फोटो </span>
              <span className="text-orange-400">गैलरी</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              विभिन्न कार्यक्रमों और उत्सवों की यादें - हर तस्वीर एक कहानी कहती है।
            </p>
          </div>
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md mx-auto">
              <p className="text-lg">{error}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="gallery" className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <SectionTitle>फोटो गैलरी</SectionTitle>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            विभिन्न कार्यक्रमों और उत्सवों की यादें - हर तस्वीर एक कहानी कहती है।
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setCurrentCategory(category.id)}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 border-2 ${
                currentCategory === category.id
                  ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg border-orange-600'
                  : 'border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {filteredImages.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto">
              <div className="text-6xl mb-4">📷</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">कोई फोटो नहीं मिला</h3>
              <p className="text-gray-500 mb-6">
                {currentCategory === 'all' 
                  ? 'अभी तक कोई फोटो अपलोड नहीं किया गया है।' 
                  : `इस श्रेणी में कोई फोटो नहीं है।`
                }
              </p>
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          {filteredImages.map((image, index) => (
            <div
              key={image.id}
              className="group cursor-pointer overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 rounded-2xl border-0 bg-white"
              onClick={() => openLightbox(index)}
            >
              <div className="p-0 relative">
                <img
                    src={`${config.url}${image.image_url}`}
                  alt={image.title}
                  className="w-full h-64 sm:h-72 md:h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center">
                  <div className="text-white text-center p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-lg font-bold mb-2">{image.title}</h3>
                      {image.description && (
                        <p className="text-sm text-gray-200 mb-2 line-clamp-2">{image.description}</p>
                      )}
                    <div className="w-8 h-0.5 bg-orange-400 mx-auto"></div>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* View All Gallery Button */}
        {galleryData.length > 6 && (
          <Button to="/gallery" size="sm" rightIcon={ArrowRight} className="mt-8">और देखें</Button>
        )}

        {/* Lightbox */}
        {selectedImage !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="w-8 h-8" />
            </button>
            
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            <div className="max-w-4xl max-h-full">
              <img
                src={`${config.url}${filteredImages[selectedImage].image_url}`}
                alt={filteredImages[selectedImage].title}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Found';
                }}
              />
              <div className="text-white text-center mt-4">
                <h3 className="text-xl font-semibold">
                  {filteredImages[selectedImage].title}
                </h3>
                {filteredImages[selectedImage].description && (
                  <p className="text-gray-200 mt-2">
                    {filteredImages[selectedImage].description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;